/**
 * useConfetti Hook
 * Triggers celebration confetti animation on wheel win
 * Supports both normal and fullscreen modes
 */
import { useCallback, useRef } from 'react';

// canvas-confetti is only needed after a spin result, never for the initial
// render, so it's loaded on demand instead of shipping in the main bundle
// (was previously delaying hydration/LCP for every visitor, spinner or not).
let confettiModulePromise = null;
const loadConfetti = () => {
  if (!confettiModulePromise) {
    confettiModulePromise = import('canvas-confetti').then((mod) => mod.default);
  }
  return confettiModulePromise;
};

const useConfetti = () => {
  // Store confetti instance to avoid recreating it
  const confettiInstanceRef = useRef(null);
  const canvasIdRef = useRef(0);

  // Get confetti options based on fullscreen mode
  const getConfettiCanvas = useCallback(async () => {
    const confetti = await loadConfetti();

    // Check if we're in fullscreen mode
    const isFullscreen = document.querySelector('.fullscreen-mode') !== null;

    if (isFullscreen) {
      // Create a NEW canvas each time to avoid transferControlToOffscreen issues
      // Remove any existing fullscreen confetti canvas
      const existingCanvas = document.getElementById('fullscreen-confetti-canvas');
      if (existingCanvas) {
        existingCanvas.remove();
      }
      
      // Create fresh canvas with unique ID
      canvasIdRef.current += 1;
      const canvas = document.createElement('canvas');
      canvas.id = 'fullscreen-confetti-canvas';
      canvas.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        pointer-events: none;
        z-index: 100000;
      `;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      document.body.appendChild(canvas);
      
      // Create confetti instance WITHOUT useWorker to avoid transferControlToOffscreen
      return confetti.create(canvas, { resize: false, useWorker: false });
    }
    
    // Use default global confetti for normal mode
    return confetti;
  }, []);

  const fireConfetti = useCallback(async (options = {}) => {
    const confettiInstance = await getConfettiCanvas();
    const defaults = {
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      zIndex: 100000
    };

    // Basic confetti burst
    confettiInstance({
      ...defaults,
      ...options
    });
  }, [getConfettiCanvas]);

  // Celebration animation - multiple bursts (enhanced for fullscreen)
  const celebrate = useCallback(async () => {
    const confettiInstance = await getConfettiCanvas();
    const isFullscreen = document.querySelector('.fullscreen-mode') !== null;
    
    const duration = isFullscreen ? 4000 : 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFEAA7', '#DDA0DD', '#F8B500', '#FF69B4', '#00FF7F'];

    // Enhanced particle count for fullscreen
    const particleMultiplier = isFullscreen ? 2 : 1;

    const frame = () => {
      confettiInstance({
        particleCount: 4 * particleMultiplier,
        angle: 60,
        spread: 65,
        origin: { x: 0 },
        colors: colors,
        zIndex: 100000
      });
      confettiInstance({
        particleCount: 4 * particleMultiplier,
        angle: 120,
        spread: 65,
        origin: { x: 1 },
        colors: colors,
        zIndex: 100000
      });

      if (Date.now() < animationEnd) {
        requestAnimationFrame(frame);
      }
    };

    // Initial big burst - bigger for fullscreen
    confettiInstance({
      particleCount: 150 * particleMultiplier,
      spread: isFullscreen ? 180 : 100,
      origin: { y: 0.5, x: 0.5 },
      colors: colors,
      zIndex: 100000
    });

    // Add extra center bursts for fullscreen
    if (isFullscreen) {
      setTimeout(() => {
        confettiInstance({
          particleCount: 80,
          spread: 150,
          origin: { y: 0.4, x: 0.5 },
          colors: colors,
          zIndex: 100000
        });
      }, 200);
      
      setTimeout(() => {
        confettiInstance({
          particleCount: 80,
          spread: 150,
          origin: { y: 0.6, x: 0.5 },
          colors: colors,
          zIndex: 100000
        });
      }, 400);
    }

    // Side streams
    frame();
  }, [getConfettiCanvas]);

  // Fireworks style (enhanced for fullscreen)
  const fireworks = useCallback(async () => {
    const confettiInstance = await getConfettiCanvas();
    const isFullscreen = document.querySelector('.fullscreen-mode') !== null;
    
    const duration = isFullscreen ? 3500 : 2500;
    const animationEnd = Date.now() + duration;
    const defaults = { 
      startVelocity: isFullscreen ? 45 : 30, 
      spread: 360, 
      ticks: isFullscreen ? 80 : 60, 
      zIndex: 100000 
    };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = (isFullscreen ? 80 : 50) * (timeLeft / duration);
      
      // Random position fireworks
      confettiInstance({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confettiInstance({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
      
      // Add center fireworks for fullscreen
      if (isFullscreen) {
        confettiInstance({
          ...defaults,
          particleCount: particleCount * 0.5,
          origin: { x: randomInRange(0.4, 0.6), y: Math.random() - 0.2 }
        });
      }
    }, isFullscreen ? 200 : 250);
  }, [getConfettiCanvas]);

  // School pride (two colors)
  const schoolPride = useCallback(async (color1 = '#FF6B6B', color2 = '#4ECDC4') => {
    const confettiInstance = await getConfettiCanvas();
    const isFullscreen = document.querySelector('.fullscreen-mode') !== null;
    
    const end = Date.now() + (isFullscreen ? 3000 : 2000);
    const colors = [color1, color2];
    const particleMultiplier = isFullscreen ? 2 : 1;

    const frame = () => {
      confettiInstance({
        particleCount: 3 * particleMultiplier,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors,
        zIndex: 100000
      });
      confettiInstance({
        particleCount: 3 * particleMultiplier,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors,
        zIndex: 100000
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, [getConfettiCanvas]);

  return {
    fireConfetti,
    celebrate,
    fireworks,
    schoolPride
  };
};

export default useConfetti;
